import CustomButton from "components/common/Button/CustomButton";
import MultiFileInput from "components/common/FileUpload/MultiFileInput";
import { useState } from "react";
import { AiOutlineWarning } from "react-icons/ai";
import { FiCheckCircle, FiEdit2 } from "react-icons/fi";
import SignatureCanvas from "react-signature-canvas";

export const AttachmentsStep = ({
  attachments,
  handleFileChange,
  removeAttachment,
  signaturePadRef,
  saveSignature,
  clearSignature,
  signatureData,
  register,
  watch,
  setValue,
  setAttachments,
}) => {
  // Track if user wants to re-sign (when editing with existing signature)
  const [isResigning, setIsResigning] = useState(false);

  // Only show "Existing signature" + "Re-sign" when editing an incident (signature loaded from server as URL).
  // When creating, or when user just drew (data URL), always show the canvas — no Re-sign block.
  const isExistingSignature =
    signatureData && signatureData.startsWith("http") && !isResigning;

  // Adapter function for MultiFileInput's handleFile (receives array, not event)
  const handleFile = (filesArray) => {
    setAttachments(filesArray);
  };

  // Adapter function for MultiFileInput's handleDelete (receives updated array)
  const handleDelete = (filesArray) => {
    setAttachments(filesArray);
  };

  // Handle re-sign action
  const handleResign = () => {
    setIsResigning(true);
    clearSignature();
  };

  return (
    <div className="space-y-6 animate-fadeIn">
      {/* Section: Signatory Name */}
      {register && (
        <section>
          <h3 className="text-sm font-semibold text-gray-900 mb-4">Name</h3>
          <input
            type="text"
            id="signatory_name"
            {...register("signatory_name")}
            className="block w-full text-sm border border-gray-300 rounded-md px-3 py-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter signatory name"
          />
        </section>
      )}

      {/* Section: File Upload */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Attachments{" "}
          <span className="text-gray-400 font-normal">(Max 5 files)</span>
        </h3>

        <MultiFileInput
          filenameLimit={150}
          type="any"
          handleFile={handleFile}
          size={5}
          files={attachments}
          handleDelete={handleDelete}
          showDragAndDrop={false}
          maxFilesAllow={5}
        />
      </section>

      {/* Section: Signature */}
      <section>
        <h3 className="text-sm font-semibold text-gray-900 mb-4">
          Signature <span className="text-red-500">*</span>
        </h3>

        {/* Show existing signature image if available and not re-signing */}
        {isExistingSignature && !isResigning ? (
          <div className="border-2 border-gray-300 rounded-xl bg-gray-50 overflow-hidden p-4">
            <div className="flex items-center justify-between mb-3">
              <span className="text-green-600 text-sm flex items-center gap-1">
                <FiCheckCircle /> Existing signature
              </span>
              <CustomButton
                type="button"
                variant="secondary"
                label="Re-sign"
                Icon={<FiEdit2 className="w-4 h-4" />}
                clickHandler={handleResign}
              />
            </div>
            <div className="bg-white rounded-lg p-2 border border-gray-200">
              <img
                src={signatureData}
                alt="Existing Signature"
                className="max-h-40 mx-auto"
              />
            </div>
          </div>
        ) : (
          <>
            <div className="border-2 border-gray-300 rounded-xl bg-gray-50 overflow-hidden">
              <SignatureCanvas
                ref={signaturePadRef}
                canvasProps={{ className: "w-full h-48 bg-white" }}
                onEnd={saveSignature}
              />
            </div>
            <div className="flex justify-between items-center mt-3">
              <CustomButton
                type="button"
                variant="secondary"
                label="Clear Signature"
                clickHandler={clearSignature}
              />
              {signatureData && !isExistingSignature && (
                <span className="text-green-600 text-sm flex items-center gap-1">
                  <FiCheckCircle /> Signature captured
                </span>
              )}
            </div>
          </>
        )}
      </section>

      {/* Declaration */}
      <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-5">
        <div className="flex items-start gap-3">
          <AiOutlineWarning className="w-6 h-6 text-yellow-600 flex-shrink-0" />
          <div>
            <h4 className="font-semibold text-yellow-800">Declaration</h4>
            <p className="text-sm text-yellow-700 mt-1">
              By signing and submitting this form, I confirm that all
              information provided is accurate and complete to the best of my
              knowledge.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
